import { createFileRoute } from "@tanstack/react-router";
import { aabb2ByTwoPoints } from "geom/aabb2";
import { aabb3Center } from "geom/aabb3";
import type { Face } from "geom/brep";
import { brepCopy, brepMove, faceAABB3, facesAABB3 } from "geom/brep";
import { mat4Rotate } from "geom/mat4";
import { planeCopy, planeMove } from "geom/plane";
import type { DCEL } from "geom/polyclip/dcel";
import { renderSetOps3TestCaseIntoCanvas } from "geom/polyclip/render";
import type {
  BRepBooleanOp,
  BRepIntersectAllResult,
  BuiltFaceDCEL,
  FacePlaneIntersection,
} from "geom/polyclip/setOps3";
import {
  booleanBRep,
  brepsIntersectAll,
  dcelsFromIntersectionResult,
  mapBRepToIndexedFaceBRep,
} from "geom/polyclip/setOps3";
import { BRepSetOpsTestCases, BRepSetOpsTestCaseUnitTests } from "geom/polyclip/test/testCasesBrepSetOps";
import { transformBRep } from "geom/transform";
import { defaultCameraSettings } from "gfx/CameraSettings";
import { faceToTHREE, polyLine2ToTHREE } from "gfx/convert";
import { EnvPreset } from "gfx/EnvPreset";
import { envPresetSceneLights } from "gfx/sceneLights";
import THREEViewer from "gfx/THREEViewer";
import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import {
  BackSide,
  DoubleSide,
  FrontSide,
  Group,
  LineBasicMaterial,
  MeshBasicMaterial,
  MeshLambertMaterial,
  type Mesh,
} from "three";
import colors from "ui/styles/colors";
import Canvas from "./-Canvas";

function BRepSetOpsTestPage() {
  const [viewer, setViewer] = useState<THREEViewer | null>(null);
  const viewerContainer = useRef<HTMLDivElement>(null);
  const [testIndex, setTestIndex] = useState(0);
  const [showDCELFaces, setShowDCELFaces] = useState(false);
  const [showResult, setShowResult] = useState(true);
  const [showInput, setShowInputs] = useState(false);
  const [explodeDistance, setExplodeDistance] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [translateZ, setTranslateZ] = useState(0);
  const [rotateZ, setRotateZ] = useState(0);
  const [setOpType, setSetOpType] = useState<BRepBooleanOp>("UNION");

  const testCases = [
    ...BRepSetOpsTestCases,
    ...BRepSetOpsTestCaseUnitTests.map((x) => ({ ...x, name: "(UNIT TEST) " + x.name })),
  ];

  useEffect(() => {
    const viewer = new THREEViewer({
      container: viewerContainer.current,
      options: {
        gui: false,
        shadows: false,
        animate: true,
        showAxes: true,
        ssfxRenderer: false,
        debugFeaturesEnabled: false,
        sceneLights: envPresetSceneLights[EnvPreset.WHITEBOX_LOW_FOG],
        cameraSettings: {
          ...defaultCameraSettings,
          targetX: 0,
          targetY: 0,
          targetZ: 0,
          x: 700,
          y: -700,
          z: 700,
          cameraNear: 1,
          cameraFar: 5000,
        },
      },
    });
    viewer.controls.maxPolarAngle = Math.PI;
    viewer.controls.update();

    setViewer(viewer);

    return () => {
      viewer.dispose();
      setViewer(null);
    };
  }, []);

  const [intersectResults, setIntersectResults] = useState<BRepIntersectAllResult | null>(null);
  const [faceDCELs, setFaceDCELs] = useState<BuiltFaceDCEL[] | null>(null);
  const [setOpResult, setSetOpResult] = useState<Face[] | null>(null);

  function getTotalFaceCount(testIndex: number): number {
    const c = testCases[testIndex];
    return c.a.faces.length + c.b.faces.length;
  }

  function getTestCase(testIndex: number) {
    const c = testCases[testIndex];

    const rotate = mat4Rotate(rotateZ / (2 * Math.PI), 0, 0, 1);
    const brep = rotate ? transformBRep(brepCopy(c.b), rotate) : brepCopy(c.b);

    return {
      ...c,
      b: brepMove(brep, translateX, translateY, translateZ),
    };
  }

  useEffect(() => {
    const c = getTestCase(testIndex);

    // uncomment this to measure performance

    // const PERF_MEASURE_COUNT = 100;

    // const d0 = performance.now();
    // for (let i = 0; i < PERF_MEASURE_COUNT; i++) {
    //   booleanBRep(c.a, c.b, setOpType);
    // }
    // const d1 = performance.now();
    // console.log("time for op", (d1 - d0) / PERF_MEASURE_COUNT, "ms");

    // intermediate data structures useful for debugging
    const { indexedFaceBRep: aIndexed, lastIndex: aLastIndex } = mapBRepToIndexedFaceBRep(c.a);
    const { indexedFaceBRep: bIndexed } = mapBRepToIndexedFaceBRep(c.b, aLastIndex);
    const result = brepsIntersectAll(aIndexed, bIndexed, setOpType);
    const dcels = dcelsFromIntersectionResult(aIndexed, bIndexed, result);

    setSetOpResult(booleanBRep(c.a, c.b, setOpType));

    setIntersectResults(result);
    setFaceDCELs(dcels);
  }, [testIndex, setOpType, translateX, translateY, translateZ, rotateZ]);

  useEffect(() => {
    if (!viewer || !showInput) {
      return undefined;
    }

    const c = getTestCase(testIndex);

    let id = 0;

    const group = new Group();

    const mata = new MeshLambertMaterial({
      color: colors.green,
      side: FrontSide,
    });

    const matb = new MeshLambertMaterial({
      color: colors.orange,
      side: FrontSide,
    });

    for (const face of c.a.faces) {
      group.add(faceToTHREE(face, mata, id++));
    }

    for (const face of c.b.faces) {
      group.add(faceToTHREE(face, matb, id++));
    }

    viewer.add(group);

    return () => {
      viewer.remove(group);
    };
  }, [viewer, testIndex, showInput, translateX, translateY, translateZ, rotateZ]);

  useEffect(() => {
    if (!viewer) {
      return undefined;
    }

    const c = getTestCase(testIndex);

    const group = new Group();

    for (const face of c.a.faces) {
      for (const loop of face.loops) {
        group.add(
          polyLine2ToTHREE({
            path: loop,
            plane: face.plane,
            material: new LineBasicMaterial({
              color: "black",
              polygonOffset: true,
            }),
          }),
        );
      }
    }

    for (const face of c.b.faces) {
      for (const loop of face.loops) {
        group.add(
          polyLine2ToTHREE({
            path: loop,
            plane: face.plane,
            material: new LineBasicMaterial({
              color: "black",
              polygonOffset: true,
            }),
          }),
        );
      }
    }

    viewer.add(group);

    return () => {
      viewer.remove(group);
    };
  }, [viewer, testIndex, translateX, translateY, translateZ, rotateZ]);

  useEffect(() => {
    if (!viewer || !setOpResult || !showResult) {
      return undefined;
    }

    const group = new Group();

    if (setOpResult.length === 0) {
      return undefined;
    }

    const [cx, cy, cz] = aabb3Center(facesAABB3(setOpResult));

    for (const face of setOpResult) {
      // Render the front & back to ensure that all faces are oriented
      // correctly
      const frontFaceMtl = new MeshLambertMaterial({
        color: colors.red,
        side: FrontSide,
        transparent: true,
        depthTest: true,
        depthWrite: true,
        polygonOffset: true,
        polygonOffsetUnits: 1,
        polygonOffsetFactor: 1,
      });

      const backFaceMtl = new MeshLambertMaterial({
        color: colors.blue,
        side: BackSide,
        transparent: true,
        depthTest: true,
        depthWrite: true,
        polygonOffset: true,
        polygonOffsetUnits: 1,
        polygonOffsetFactor: 1,
      });

      const [fx, fy, fz] = aabb3Center(faceAABB3(face));

      group.add(
        faceToTHREE(
          {
            ...face,
            plane: planeMove(
              planeCopy(face.plane),
              0.1 * (fx - cx) * explodeDistance,
              0.1 * (fy - cy) * explodeDistance,
              0.1 * (fz - cz) * explodeDistance,
            ),
          },
          frontFaceMtl,
        ),
      );

      group.add(
        faceToTHREE(
          {
            ...face,
            plane: planeMove(
              planeCopy(face.plane),
              0.1 * (fx - cx) * explodeDistance,
              0.1 * (fy - cy) * explodeDistance,
              0.1 * (fz - cz) * explodeDistance,
            ),
          },
          backFaceMtl,
        ),
      );

      for (const loop of face.loops) {
        group.add(
          polyLine2ToTHREE({
            path: loop,
            plane: face.plane,
            material: new LineBasicMaterial({
              color: "black",
            }),
          }),
        );
      }
    }

    viewer.add(group);

    return () => {
      viewer.remove(group);
    };
  }, [
    setOpType,
    viewer,
    testIndex,
    setOpResult,
    explodeDistance,
    showResult,
    translateX,
    translateY,
    translateZ,
    rotateZ,
  ]);

  const [selectedFaceIdx, setFocusedFace] = useState<number>(0);
  const [selectedFace, setSelectedFace] = useState<Face | null>(null);
  const [selectedFaceDCEL, setSelectedFaceDCEL] = useState<DCEL | "NO_ISECT" | undefined>(undefined);
  const [selectedResult, setSelectedResult] = useState<FacePlaneIntersection[] | null>(null);

  useEffect(() => {
    if (!viewer) {
      return undefined;
    }

    const c = getTestCase(testIndex);

    let result: FacePlaneIntersection[] = [];
    let face: Face | null = null;
    let dcel: DCEL | "NO_ISECT" | undefined = undefined;
    if (faceDCELs) {
      if (selectedFaceIdx < c.a.faces.length) {
        face = c.a.faces[selectedFaceIdx];
        result = intersectResults?.faceIsects[selectedFaceIdx] ?? [];
        dcel = faceDCELs.find((x) => x.isA && x.index === selectedFaceIdx)?.dcel;
      } else if (selectedFaceIdx < c.a.faces.length + c.b.faces.length) {
        const idx = selectedFaceIdx - c.a.faces.length;
        face = c.b.faces[idx];
        result = intersectResults?.oppFaceIsects[idx] ?? [];
        dcel = faceDCELs.find((x) => !x.isA && x.index === idx)?.dcel;
      }
    }
    setSelectedFace(face);
    setSelectedResult(result);
    setSelectedFaceDCEL(dcel);

    if (!face) {
      return undefined;
    }

    let mesh: Mesh;
    if (showInput) {
      mesh = faceToTHREE(
        face,
        new MeshBasicMaterial({
          color: colors.blue,
          transparent: true,
          opacity: 1,
          side: DoubleSide,
        }),
        selectedFaceIdx ?? undefined,
      );

      viewer.add(mesh);
    }
    return () => {
      if (showInput) {
        viewer.remove(mesh);
      }
    };
  }, [
    setOpType,
    viewer,
    testIndex,
    selectedFaceIdx,
    showDCELFaces,
    showInput,
    translateX,
    translateY,
    translateZ,
    rotateZ,
    faceDCELs,
  ]);

  const renderTest = useCallback(
    (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, scale: number) => {
      if (!selectedFace || !selectedResult || !selectedFaceDCEL) {
        return;
      }
      renderSetOps3TestCaseIntoCanvas({
        canvas,
        ctx,
        result: selectedResult,
        face: selectedFace,
        dcel: selectedFaceDCEL,
        showDCELFaces,
        scale,
      });
    },
    [setOpType, selectedFaceDCEL, selectedResult, selectedFace, selectedFaceIdx, showDCELFaces, testIndex, translateX],
  );

  return (
    <>
      <Head>
        <style>{`
          fieldset div {
            height: 2em;
            display: flex;
            flex-direction: row;
            align-items: center;
          }

          fieldset div label {
            width: 8em;
            text-align: right;
            padding-right: 1em;
          }
        `}</style>
      </Head>
      <div
        style={{
          padding: 5,
          border: "1px solid black",
          position: "absolute",
          top: 0,
          right: 0,
          width: 700,
          background: "white",
          zIndex: 100,
          display: selectedFaceIdx === null ? "none" : "block",
        }}
        ref={viewerContainer}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
          <label htmlFor="selectedFaceIdx">Selected Face</label>
          <input
            type="number"
            value={selectedFaceIdx ?? undefined}
            min={0} // Set your minimum value
            max={getTotalFaceCount(testIndex) - 1} // Set your maximum value
            step={1} // Ensures it's an integer value
            onChange={(e) => setFocusedFace(parseInt(e.target.value, 10))}
            style={{
              width: "60px",
              padding: "5px",
              borderRadius: "4px",
              border: "1px solid #ccc",
            }}
          />
        </div>
        <Canvas canvasWidth={700} canvasHeight={700} render={renderTest} />
        <div>
          <label htmlFor="testCase">Show DCEL faces</label>
          <input type="checkbox" checked={showDCELFaces} onChange={(x) => setShowDCELFaces(x.target.checked)}></input>
        </div>
      </div>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 100,
        }}
        ref={viewerContainer}
      >
        <fieldset style={{ display: "flex", flexDirection: "column" }}>
          <div>
            <label htmlFor="testCase">Test case</label>
            <select
              name="testCase"
              value={testIndex}
              onChange={(x) => {
                setTestIndex(parseInt(x.target.value));
                const test = getTestCase(parseInt(x.target.value));
                if (test.translate) {
                  setTranslateX(test.translate[0]);
                  setTranslateY(test.translate[1]);
                  setTranslateZ(test.translate[2]);
                } else {
                  setTranslateX(0);
                  setTranslateY(0);
                  setTranslateZ(0);
                }

                if (test.rotate) {
                  setRotateZ(test.rotate[2]);
                } else {
                  setRotateZ(0);
                }

                if (test.operation) {
                  setSetOpType(test.operation);
                } else {
                  setSetOpType("UNION");
                }
              }}
            >
              {testCases.map((c, i) => (
                <option key={i} value={i}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="setop">Operation</label>
            <select
              name="setop"
              value={setOpType}
              onChange={(x) => {
                setSetOpType(x.target.value as BRepBooleanOp);
              }}
            >
              {["UNION", "SUBTRACT", "INTERSECT"].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="testCase">Show input</label>
            <input type="checkbox" checked={showInput} onChange={(x) => setShowInputs(x.target.checked)}></input>
          </div>
          <div>
            <label htmlFor="testCase">Show result</label>
            <input type="checkbox" checked={showResult} onChange={(x) => setShowResult(x.target.checked)}></input>
          </div>
          <div>
            <label htmlFor="testCase">Explode result distance</label>
            <input
              name="explodeDistance"
              type="range"
              step={0.001}
              min={0}
              max={50}
              value={explodeDistance}
              onChange={(x) => setExplodeDistance(parseFloat(x.target.value))}
            />
          </div>
          <div>
            <label htmlFor="translateX">Translate X</label>
            <input
              name="translateX"
              type="range"
              step={0.001}
              min={-200}
              max={200}
              value={translateX}
              onChange={(x) => setTranslateX(parseFloat(x.target.value))}
            />
            <input
              type="number"
              value={translateX}
              min={-200}
              max={200}
              step={0.001}
              onChange={(e) => setTranslateX(parseFloat(e.target.value))}
              style={{
                width: "80px",
                padding: "5px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>
          <div>
            <label htmlFor="translateY">Translate Y</label>
            <input
              name="translateY"
              type="range"
              step={0.001}
              min={-200}
              max={200}
              value={translateY}
              onChange={(x) => setTranslateY(parseFloat(x.target.value))}
            />
            <input
              type="number"
              value={translateY}
              min={-200}
              max={200}
              step={0.001}
              onChange={(e) => setTranslateY(parseFloat(e.target.value))}
              style={{
                width: "80px",
                padding: "5px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>
          <div>
            <label htmlFor="translateZ">Translate Z</label>
            <input
              name="translateZ"
              type="range"
              step={0.001}
              min={-200}
              max={200}
              value={translateZ}
              onChange={(x) => setTranslateZ(parseFloat(x.target.value))}
            />
            <input
              type="number"
              value={translateZ}
              min={-200}
              max={200}
              step={0.001}
              onChange={(e) => setTranslateZ(parseFloat(e.target.value))}
              style={{
                width: "80px",
                padding: "5px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>
          <div>
            <label htmlFor="rotateZ">Rotate Z</label>
            <input
              name="rotateZ"
              type="range"
              step={0.001}
              min={-180}
              max={180}
              value={rotateZ}
              onChange={(x) => setRotateZ(parseFloat(x.target.value))}
            />
            <input
              type="number"
              value={rotateZ}
              min={-180}
              max={180}
              step={0.001}
              onChange={(e) => setRotateZ(parseFloat(e.target.value))}
              style={{
                width: "80px",
                padding: "5px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>
        </fieldset>
      </div>
      <div
        onMouseUp={({ clientX, clientY }) => {
          if (viewer) {
            const clientBb = aabb2ByTwoPoints(clientX, clientY, clientX + 0.1, clientY + 0.1);
            const res = viewer.pickEntityIds(clientBb);
            const test = getTestCase(testIndex);
            if (res.length > 0 && res[0] < test.a.faces.length + test.b.faces.length) {
              setFocusedFace(res[0]);
            }
          }
        }}
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
        ref={viewerContainer}
      />
    </>
  );
}

export const Route = createFileRoute("/debug/polyclip/brep-set-ops-test")({
  component: BRepSetOpsTestPage,
});
